import {
  Stack,
  useDevice,
  useViewport,
  Viewport,
  AccordionItemType,
} from '@treatwell/ui';
import { CmsText } from 'js/components/CmsText/CmsText';
import { FAQPageSchema } from 'js/components/FAQPageSchema';
import { FAQsSection } from 'js/components/FAQsSection';
import { HighlightedText } from 'js/components/HighlightedText/HighlightedText';
import { Context } from 'js/components/LocaleWrapper';
import { useTrackPageAndSessionView } from 'js/hooks/useTrackPageAndSessionView';
import {
  CmsNearMePageType,
  CmsNearMeTreatmentType,
} from 'js/model/cms/cms-near-me-page';
import { NearMeFAQType } from 'js/model/rainbow/near-me/NearMePageModel';
import React from 'react';
import barberSummaryIcon from './assets/barber-summary.svg';
import beautySummaryIcon from './assets/beauty-summary.svg';
import hairSummaryIcon from './assets/hair-summary.svg';
import longSummaryIcon from './assets/long-summary.svg';
import massageSummaryIcon from './assets/massage-summary.svg';
import { CityList } from './CityList';
import styles from './NearMePage.module.css';
import { SearchByCity } from './SearchByCity';
import { Summary } from './Summary';
import { NearMeSummaryType } from './Summary/Summary';
import { VenueCardList } from './VenueCardList';
import { VenueCardListType } from './VenueCardList/VenueCardList';

const treatmentIconMap = {
  barber: barberSummaryIcon,
  beauty: beautySummaryIcon,
  hair: hairSummaryIcon,
  massage: massageSummaryIcon,
};

const ShortSummary = ({ icon, text }: NearMeSummaryType) => (
  <div className={styles.shortSummary}>
    <Summary icon={icon} text={text} />
  </div>
);

const LongSummary = ({ icon, text }: NearMeSummaryType) => (
  <div className={styles.longSummary}>
    <Summary icon={icon} text={text} />
  </div>
);

const VenueCardSections = ({
  sections,
  countryCode,
}: {
  sections: VenueCardListType;
  countryCode: string;
}) => (
  <div className={styles.venueCardSections}>
    <VenueCardList venueCardSections={sections} countryCode={countryCode} />
  </div>
);

const generateFAQs = (faqs: NearMeFAQType[]): AccordionItemType[] =>
  faqs.map(({ question, answer }, index) => ({
    id: `faq-${index}`,
    title: question,
    content: <CmsText text={answer} />,
  }));

export const NearMePage = () => {
  const { pageData } = React.useContext(Context);
  const { isMobile, isDesktop } = useDevice();
  const isMobileViewport = useViewport({
    device: ['mobile', 'tablet'],
    serverRender: isMobile,
  });
  useTrackPageAndSessionView();
  const cms: CmsNearMeTreatmentType =
    pageData.cms['near-me'][pageData.nearMe.treatment];
  const cmsNearMePage: CmsNearMePageType = pageData.cms.page['near-me'];
  const venueSections = pageData.nearMe.venueSections;
  const treatment = pageData.nearMe.treatment;
  const sections: VenueCardListType = [];

  venueSections.forEach((venueSection, index) => {
    if (venueSection.length > 0) {
      sections.push({
        renderTitle: (titleHighlightedClassName) => (
          <HighlightedText
            className={titleHighlightedClassName}
            text={cms.section[index].title}
          />
        ),
        venueCards: venueSection,
      });
    }
  });

  return (
    <>
      <SearchByCity
        renderTitle={(className) => (
          <HighlightedText className={className} text={cms['page-title']} />
        )}
      />
      <div className={styles.pageContainer}>
        <Viewport device="mobile" serverRender={isMobile}>
          <VenueCardSections
            sections={sections}
            countryCode={pageData.channel.country.countryCode}
          />
          <ShortSummary
            icon={treatmentIconMap[treatment]}
            text={cms['short-summary']}
          />
          <LongSummary icon={longSummaryIcon} text={cms['long-summary']} />
        </Viewport>
        <Viewport device={['tablet', 'desktop']} serverRender={isDesktop}>
          <ShortSummary
            icon={treatmentIconMap[treatment]}
            text={cms['short-summary']}
          />
          <VenueCardSections
            sections={sections}
            countryCode={pageData.channel.country.countryCode}
          />
          <LongSummary icon={longSummaryIcon} text={cms['long-summary']} />
        </Viewport>
      </div>
      <Stack
        space={isMobileViewport ? 'md' : 'xl'}
        className={styles.preFooter}
      >
        {pageData.nearMe.faqs.length > 0 && (
          <>
            <FAQsSection
              header={{
                title: cms.faqs.title,
                description: cms.faqs.description,
              }}
              faqs={generateFAQs(pageData.nearMe.faqs)}
              showAllFAQsButtonLabel={cmsNearMePage['see-all-faqs']}
              className={styles.faqs}
            />
            <FAQPageSchema faqs={pageData.nearMe.faqs} />
          </>
        )}
        {pageData.nearMe.cities.length > 0 && (
          <CityList
            renderTitle={(title, className) => (
              <HighlightedText className={className} text={title} />
            )}
          />
        )}
      </Stack>
    </>
  );
};
